import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  BackLink,
  Divider,
  Lightbulb,
  ChevronDown,
  ArrowRight,
  Check,
  Tile
} from "@/components/ds";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

// Exported nav helper

export function BackToIntegrationLandingGuesty() {
  return <BackLink to="/integrations" label="Back to Integrations" />;
}

// Types

interface GuestyConfig {
  id: number;
  siteId: number;
  displayName: string;
  apiTokenMasked: string;
  enabled: boolean;
  isActive?: boolean;
  listingCount?: number;
  pushTranscripts: boolean;
  createdAt: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "field-reference" | "logs";

// Capabilities

const CAPABILITIES = [
  {
    title: "Reservation & guest management",
    icon: "Building",
    items: [
      "Find any reservation instantly by ID, confirmation code, or guest email — full details including dates, amounts, and property",
      "Guest profiles with star rating, review history, stay count, and OTA verification status",
      "Booking history across all properties in the portfolio — past stays, upcoming arrivals, and cancellation history",
      "guesty_get_reservation / guesty_search_reservations — AI looks up reservations automatically during any guest conversation"
    ]
  },
  {
    title: "Check-in & guest experience",
    icon: "Tag",
    items: [
      "Retrieve door codes, lockbox combinations, WiFi passwords, parking instructions, and arrival guides for any listing",
      "House rules for every listing — pets, smoking, parties, quiet hours, and checkout procedures",
      "guesty_send_message — AI sends messages via Guesty's unified inbox, reaching guests on Airbnb, VRBO, Booking.com, and direct",
      "Guest reviews — retrieve host reviews and rating summaries to build trust during the booking conversation"
    ]
  },
  {
    title: "Listing & availability intelligence",
    icon: "Calendar",
    items: [
      "Real-time availability check across all Guesty-managed channels — zero double-booking risk",
      "Pricing for any listing on specific dates — nightly rate, cleaning fee, OTA commission, and total payout",
      "guesty_create_quote — generate a full pricing quote and send a direct booking link to close hesitant guests",
      "guesty_get_calendar — month-view availability calendar for scheduling and stay-extension conversations"
    ]
  },
  {
    title: "Operations & owner reporting",
    icon: "FileText",
    items: [
      "Maintenance and cleaning task status for any listing — know which properties are ready for check-in",
      "guesty_get_owner_statement — monthly revenue summary per property for owner reporting conversations",
      "Financial report — gross revenue, OTA commissions, cleaning fees, and net payout per booking",
      "guesty_create_reservation / guesty_cancel_reservation — take or cancel direct bookings from the chat"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "Zap",
    items: [
      "20 tools available: get_reservation, search_reservations, get_guest, check_availability, get_listing, get_pricing, get_checkin_instructions, get_house_rules, send_message, get_reviews, get_financial_report, create_reservation, cancel_reservation, get_tasks, get_calendar, create_quote, get_listings, get_guest_history, update_reservation_notes, get_owner_statement",
      "No workflow configuration required — AI decides when to call Guesty based on conversation context",
      "Supports enterprise portfolios of hundreds of listings across multiple Guesty accounts",
      "Field mapping is optional — AI works immediately; mapping adds agent sidebar and HTTP POST variables"
    ]
  }
];

// AI Tools

const AI_TOOLS = [
  {
    name: "guesty_get_reservation",
    label: "Get reservation details",
    group: "Reservations",
    description:
      "Full reservation by ID or confirmation code — guest, dates, listing, channel, amounts, and status."
  },
  {
    name: "guesty_search_reservations",
    label: "Search reservations",
    group: "Reservations",
    description:
      "Find reservations by guest email, date range, or status. Returns IDs, dates, and listing names."
  },
  {
    name: "guesty_create_reservation",
    label: "Create direct reservation",
    group: "Reservations",
    description:
      "Book a direct reservation in Guesty — bypasses OTAs to save commission. Returns confirmation number."
  },
  {
    name: "guesty_cancel_reservation",
    label: "Cancel reservation",
    group: "Reservations",
    description:
      "Cancel a reservation with policy check. Returns cancellation fee and refund amount per policy."
  },
  {
    name: "guesty_update_reservation_notes",
    label: "Update reservation notes",
    group: "Reservations",
    description:
      "Add internal staff notes to a reservation — visible in Guesty only, not shared with the guest."
  },
  {
    name: "guesty_get_guest",
    label: "Get guest profile",
    group: "Guests",
    description:
      "Guest profile: name, email, phone, OTA verification status, star rating, and total past stays."
  },
  {
    name: "guesty_get_guest_history",
    label: "Guest stay history",
    group: "Guests",
    description:
      "All past and upcoming reservations for a guest across every listing in the portfolio."
  },
  {
    name: "guesty_get_reviews",
    label: "Get reviews",
    group: "Guests",
    description:
      "Retrieve guest and host reviews — star ratings, review text, and highlights. Builds trust in booking conversations."
  },
  {
    name: "guesty_check_availability",
    label: "Check availability",
    group: "Availability",
    description:
      "Real-time availability for a listing on specific dates across all connected OTA channels."
  },
  {
    name: "guesty_get_calendar",
    label: "Get availability calendar",
    group: "Availability",
    description:
      "Monthly calendar view — booked, blocked, and open nights for any listing."
  },
  {
    name: "guesty_get_listing",
    label: "Get listing details",
    group: "Listings",
    description:
      "Full listing info: bedrooms, bathrooms, amenities, address, cancellation policy, and photos URL."
  },
  {
    name: "guesty_get_listings",
    label: "List all listings",
    group: "Listings",
    description:
      "All managed listings with names, types, locations, and basic availability."
  },
  {
    name: "guesty_get_pricing",
    label: "Get pricing for dates",
    group: "Pricing",
    description:
      "Nightly rate, cleaning fee, OTA commission, and total for specific dates on any listing."
  },
  {
    name: "guesty_create_quote",
    label: "Create booking quote",
    group: "Pricing",
    description:
      "Generates a pricing quote with a direct booking link — enables commission-free direct bookings from chat."
  },
  {
    name: "guesty_get_checkin_instructions",
    label: "Get check-in instructions",
    group: "Guest Experience",
    description:
      "Access codes, lockbox combinations, WiFi, parking, arrival guide, and check-in/out times."
  },
  {
    name: "guesty_get_house_rules",
    label: "Get house rules",
    group: "Guest Experience",
    description:
      "Listing-specific house rules — pets, smoking, parties, quiet hours, and checkout procedures."
  },
  {
    name: "guesty_send_message",
    label: "Send message to guest",
    group: "Guest Experience",
    description:
      "Sends a message via Guesty's unified inbox — reaches guest on Airbnb, VRBO, Booking.com, or email."
  },
  {
    name: "guesty_get_tasks",
    label: "Get maintenance/cleaning tasks",
    group: "Operations",
    description:
      "Open maintenance and cleaning tasks for a listing — status, assignee, and scheduled time."
  },
  {
    name: "guesty_get_financial_report",
    label: "Get financial report",
    group: "Operations",
    description:
      "Booking financials: gross revenue, OTA commissions, cleaning fees, and net payout."
  },
  {
    name: "guesty_get_owner_statement",
    label: "Get owner statement",
    group: "Operations",
    description:
      "Monthly revenue summary for a listing — net owner payout, expense breakdown, and occupancy rate."
  }
];

// Field Reference

const GU_FIELD_REFERENCE = [
  {
    scope: "Reservation",
    scopeKey: "reservation",
    fields: [
      {
        field: "id",
        label: "Reservation ID",
        notes: "Guesty internal reservation ID"
      },
      {
        field: "confirmation_code",
        label: "Confirmation Code",
        notes: "Guest-facing reference"
      },
      {
        field: "status",
        label: "Status",
        notes: "confirmed / cancelled / checked_in / checked_out / inquiry"
      },
      {
        field: "check_in",
        label: "Check-In Date",
        notes: "ISO date (YYYY-MM-DD)"
      },
      {
        field: "check_out",
        label: "Check-Out Date",
        notes: "ISO date (YYYY-MM-DD)"
      },
      { field: "nights", label: "Nights", notes: "Duration of stay" },
      {
        field: "guests_count",
        label: "Guest Count",
        notes: "Number of guests in booking"
      },
      {
        field: "base_price",
        label: "Base Price",
        notes: "Nightly rate total before fees"
      },
      {
        field: "total_price",
        label: "Total Price",
        notes: "Gross booking amount"
      },
      {
        field: "host_payout",
        label: "Host Payout",
        notes: "Net after OTA commission and fees"
      },
      {
        field: "channel",
        label: "Booking Channel",
        notes: "airbnb / vrbo / bookingcom / direct / other"
      },
      {
        field: "source",
        label: "Booking Source",
        notes: "OTA name or 'direct'"
      }
    ]
  },
  {
    scope: "Guest",
    scopeKey: "guest",
    fields: [
      {
        field: "full_name",
        label: "Full Name",
        notes: "Guest display name from OTA or direct"
      },
      { field: "email", label: "Email", notes: "Contact email" },
      { field: "phone", label: "Phone", notes: "Mobile number if provided" },
      {
        field: "picture",
        label: "Profile Picture URL",
        notes: "Guest photo from OTA"
      },
      {
        field: "rating",
        label: "Average Rating",
        notes: "Average star rating from host reviews"
      },
      {
        field: "trips_count",
        label: "Total Trips",
        notes: "Number of past stays"
      },
      {
        field: "verified",
        label: "ID Verified",
        notes: "Whether OTA has verified guest identity"
      }
    ]
  },
  {
    scope: "Listing",
    scopeKey: "listing",
    fields: [
      { field: "id", label: "Listing ID", notes: "Guesty internal listing ID" },
      {
        field: "title",
        label: "Listing Title",
        notes: "Public name on OTAs and Guesty"
      },
      {
        field: "address.full",
        label: "Full Address",
        notes: "Street, city, state, zip"
      },
      { field: "bedrooms", label: "Bedrooms", notes: "Number of bedrooms" },
      { field: "bathrooms", label: "Bathrooms", notes: "Number of bathrooms" },
      {
        field: "accommodates",
        label: "Max Guests",
        notes: "Maximum occupancy"
      },
      {
        field: "property_type",
        label: "Property Type",
        notes: "Apartment / House / Villa / Condo / etc."
      },
      {
        field: "cancellation_policy",
        label: "Cancellation Policy",
        notes: "Flexible / Moderate / Strict / Super Strict"
      }
    ]
  }
];

// Shared

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(`{${variable}}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ok */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded font-mono transition-colors ${copied ? "border border-green-300 bg-green-50 text-green-700" : "border border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-(--ds-primary) hover:text-(--ds-primary)"}`}
      aria-label={`Copy variable {${variable}}`}
    >
      {copied ? "Copied" : `{${variable}}`}
    </button>
  );
}

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));
  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }
  return (
    <div className="mb-4">
      <SectionCard className="!p-0">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl"
          aria-expanded={open}
        >
          <div className="flex items-center gap-3">
            <span className="font-semibold text-zinc-800">What's included</span>
            <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
              {CAPABILITIES.length} feature areas
            </span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="px-6 pb-6">
            <Divider />
            <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
              <strong>AI tools work without any configuration.</strong> Connect
              Guesty (API token) and the AI gains access to all reservations,
              listings, guest profiles, and availability in real time.
            </div>
            <div className="space-y-2">
              {CAPABILITIES.map((cap, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-zinc-200 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleSection(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                    aria-expanded={openSections.has(i)}
                  >
                    <div className="flex items-center gap-3">
                      <IconByName name={cap.icon} className="w-4 h-4" />
                      <span className="text-sm font-medium text-zinc-700">
                        {cap.title}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openSections.has(i) && (
                    <div className="px-4 pb-3 space-y-1.5">
                      <Divider />
                      {cap.items.map((item, j) => (
                        <div key={j} className="flex items-start gap-2 pt-1.5">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-green-500" />
                          <span className="text-xs text-zinc-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function HowItWorksCard() {
  return (
    <div className="mb-4">
      <SectionCard
        title="How Guesty works in Velaro"
        description="Connect once — every listing, reservation, and guest becomes AI-searchable instantly."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-zinc-200 bg-zinc-100/60 p-4 relative">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
                1
              </span>
              <span className="font-semibold text-zinc-700 text-sm">
                Connect Guesty
              </span>
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              Enter your Guesty API token — found in your Guesty dashboard under
              Settings → Integrations → API.
            </p>
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">
                API Token
              </span>
              <span className="text-zinc-400">
                from Guesty → Settings → API
              </span>
            </div>
            <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 items-center justify-center">
              <ArrowRight className="w-3 h-3 text-zinc-400" />
            </div>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                2
              </span>
              <span className="font-semibold text-green-800 text-sm">
                20 AI tools activate
              </span>
            </div>
            <p className="text-xs text-green-700 mb-3">
              Every listing and reservation is instantly searchable — no extra
              setup.
            </p>
            <div className="grid grid-cols-2 gap-1">
              {[
                "Reservations",
                "Guest profiles",
                "Availability",
                "Pricing quotes",
                "Check-in codes",
                "House rules",
                "Guest messaging",
                "Owner statements",
                "Task status",
                "Direct booking"
              ].map((t) => (
                <div
                  key={t}
                  className="flex items-center gap-1 text-[10px] text-green-700"
                >
                  <Check className="w-2.5 h-2.5 shrink-0 text-green-600" />
                  {t}
                </div>
              ))}
            </div>
            <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 items-center justify-center">
              <ArrowRight className="w-3 h-3 text-zinc-400" />
            </div>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
                3
              </span>
              <span className="font-semibold text-blue-800 text-sm">
                Scale 24/7 guest support
              </span>
            </div>
            <p className="text-xs text-blue-700 mb-3">
              Enterprise property managers get instant guest answers at scale —
              without adding staff.
            </p>
            <div className="space-y-2">
              {[
                [
                  "AI guest concierge",
                  "Answers check-in, rules, and booking questions 24/7"
                ],
                [
                  "Direct booking bot",
                  "Quotes and books guests without OTA fees"
                ],
                [
                  "Owner reporting bot",
                  "Answers owner revenue questions instantly"
                ]
              ].map(([title, desc]) => (
                <Tile key={title} density="compact" className="!items-start !border-blue-100">
                  <div>
                    <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                      {title}
                    </div>
                    <div className="text-[10px] text-blue-600 mt-0.5">{desc}</div>
                  </div>
                </Tile>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function OverviewTab({
  hasConnection,
  onConnect,
  onOpenWizard
}: {
  hasConnection: boolean;
  onConnect(): void;
  onOpenWizard(): void;
}) {
  return (
    <div>
      <CapabilitiesPanel />
      <HowItWorksCard />
      <div className="mb-4">
        {!hasConnection ? (
          <SectionCard
            title="Connect your Guesty account"
            description="Enter your API token and all your listings, reservations, and guests become instantly searchable by the AI during any conversation."
            action={
              <PrimaryButton onClick={onConnect}>Connect now</PrimaryButton>
            }
          />
        ) : (
          <SectionCard
            title="Launch starter workflow wizard"
            description="Build a Guest AI, Direct Booking Bot, or Owner Statement Bot in minutes using a pre-built Guesty template."
            action={
              <PrimaryButton onClick={onOpenWizard}>
                Launch wizard
              </PrimaryButton>
            }
          />
        )}
      </div>
    </div>
  );
}

function AiToolsTab({
  hasConnection,
  onOpenWizard
}: {
  hasConnection: boolean;
  onOpenWizard(): void;
}) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard
      title="AI tools"
      description="All tools activate automatically on connect — no configuration required."
      action={
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
          >
            {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
          </span>
          {hasConnection && (
            <PrimaryButton onClick={onOpenWizard}>
              Launch starter wizard
            </PrimaryButton>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              {group}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <Tile
                  key={tool.name}
                  density="default"
                  className="!items-start !bg-zinc-50"
                >
                  <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-(--ds-primary)" />
                  <div>
                    <div className="text-xs font-semibold text-zinc-700">
                      {tool.label}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-400 mb-1">
                      {tool.name}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {tool.description}
                    </div>
                  </div>
                </Tile>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function AccountsTab({
  configs,
  onRefresh,
  onAdd,
  onTogglePushTranscripts
}: {
  configs: GuestyConfig[];
  onRefresh(): void;
  onAdd(): void;
  onTogglePushTranscripts(config: GuestyConfig): void;
}) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete(id: number) {
    if (!confirm("Remove this Guesty connection?")) return;
    setDeleting(id);
    setError(null);
    try {
      await api.admin.delete(`Guesty/Configs/${id}`);
      onRefresh();
    } catch (err) {
      const status = err instanceof Response ? err.status : String(err);
      setError(`Failed to remove connection: ${status}`);
    } finally {
      setDeleting(null);
    }
  }

  return (
    <>
      <SectionCard
        title="Connected accounts"
        action={<PrimaryButton onClick={onAdd}>+ Add account</PrimaryButton>}
      >
        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}
        {configs.length === 0 ? (
          <div className="text-center py-12 text-zinc-400">
            <div className="flex justify-center mb-3">
              <IconByName name="Building" className="w-10 h-10 text-zinc-400" />
            </div>
            <div className="font-medium text-zinc-600 mb-1">
              No Guesty account connected
            </div>
            <p className="text-sm">
              Connect your Guesty API token to enable AI-powered guest support
              across your entire portfolio.
            </p>
            <div className="mt-4 flex justify-center">
              <PrimaryButton onClick={onAdd}>Connect Guesty</PrimaryButton>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {configs.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border border-zinc-200 p-4 flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold bg-(--ds-primary)">
                    G
                  </div>
                  <div>
                    <div className="font-medium text-zinc-800">
                      {c.displayName}
                    </div>
                    <div className="text-xs text-zinc-400 font-mono">
                      Token: {c.apiTokenMasked}
                    </div>
                    {c.listingCount != null && (
                      <div className="text-xs text-zinc-500">
                        {c.listingCount} listings synced
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                  >
                    {c.enabled ? "Active" : "Disabled"}
                  </span>
                  <GhostButton
                    onClick={() => handleDelete(c.id)}
                    disabled={deleting === c.id}
                    className="!text-red-600 hover:!text-red-700"
                  >
                    {deleting === c.id ? "Removing..." : "Remove"}
                  </GhostButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {configs.length > 0 && (
        <SectionCard title="Transcript Auto-Push" className="mt-4">
          <div className="text-sm text-zinc-500 mb-4">
            When enabled, the full chat transcript is automatically attached as
            a note on the matched reservation in Guesty after every conversation
            ends.
          </div>
          <div className="space-y-3">
            {configs.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0"
              >
                <div>
                  <div className="text-sm font-medium text-zinc-700">
                    {c.displayName}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {c.pushTranscripts
                      ? "Transcript attached to reservation notes after each conversation"
                      : "Transcript push is off for this connection"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onTogglePushTranscripts(c)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 ${c.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                  aria-label={
                    c.pushTranscripts
                      ? "Disable transcript push"
                      : "Enable transcript push"
                  }
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${c.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`}
                  />
                </button>
              </div>
            ))}
          </div>
          {configs.some((c) => c.pushTranscripts && c.enabled) && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
              Auto-push active — transcripts will appear as notes on the matched
              reservation in Guesty after each conversation.
            </div>
          )}
        </SectionCard>
      )}
    </>
  );
}

function FieldReferenceTab() {
  const [activeScope, setActiveScope] = useState("Reservation");
  const scopeData =
    GU_FIELD_REFERENCE.find((s) => s.scope === activeScope) ??
    GU_FIELD_REFERENCE[0];
  return (
    <SectionCard
      title="Field reference"
      description="Variable names for HTTP POST nodes to push Guesty data to your CRM, ticketing, or analytics platform."
    >
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4 text-xs text-blue-800">
        <div className="font-semibold mb-2">Example HTTP POST variables</div>
        <CodeBlock>{`{
  "guest_name":   "{guesty.guest.full_name}",
  "guest_email":  "{guesty.guest.email}",
  "booking_id":   "{guesty.reservation.confirmation_code}",
  "check_in":     "{guesty.reservation.check_in}",
  "check_out":    "{guesty.reservation.check_out}",
  "channel":      "{guesty.reservation.channel}",
  "host_payout":  "{guesty.reservation.host_payout}"
}`}</CodeBlock>
      </div>
      <div className="flex gap-2 mb-4">
        {GU_FIELD_REFERENCE.map((s) => {
          const active = activeScope === s.scope;
          return active ? (
            <PrimaryButton
              key={s.scope}
              onClick={() => setActiveScope(s.scope)}
              className="!px-3 !py-1.5 !text-xs"
            >
              {s.scope}
            </PrimaryButton>
          ) : (
            <SecondaryButton
              key={s.scope}
              onClick={() => setActiveScope(s.scope)}
              className="!px-3 !py-1.5 !text-xs"
            >
              {s.scope}
            </SecondaryButton>
          );
        })}
      </div>
      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div className="px-4 py-2.5 text-white text-xs font-semibold bg-(--ds-primary)">
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Field
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Label
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Variable
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}
                >
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">
                    {f.field}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2">
                    <CopyVariableButton
                      variable={`guesty.${scopeData.scopeKey}.${f.field}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-zinc-400 italic">{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}

// Main Component

export default function Guesty() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<GuestyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Guesty/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
      setLoadError(null);
    } catch (err) {
      const status = err instanceof Response ? err.status : String(err);
      setLoadError(`Failed to load Guesty accounts: ${status}`);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  async function togglePushTranscripts(config: GuestyConfig) {
    try {
      const resp = await api.admin.put(`Guesty/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts
      });
      if (!resp.ok) throw resp;
      const updated = await resp.json();
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, pushTranscripts: updated.pushTranscripts }
            : c
        )
      );
    } catch (err) {
      const status = err instanceof Response ? err.status : String(err);
      setLoadError(`Failed to update transcript push setting: ${status}`);
    }
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "accounts", label: "Accounts" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "field-reference", label: "Field Reference" },
    { id: "logs", label: "Activity Log" }
  ];

  const tabsJSX = (
    <>
      {TABS.map((tab) => (
        <IntegrationTab
          key={tab.id}
          active={activeTab === tab.id}
          onClick={() => setActiveTab(tab.id)}
        >
          {tab.label}
          {tab.id === "accounts" && configs.length > 0 && (
            <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">
              {configs.length}
            </span>
          )}
        </IntegrationTab>
      ))}
    </>
  );

  return (
    <StickyRouteHeader title="Guesty" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="guesty"
          onClose={() => setShowWizard(false)}
        />
      )}

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Guesty integration is connected — grab your embed code from
                Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/chat_window"
                className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition"
              >
                Chat Window
              </Link>
              <Link
                to="/deployment"
                className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition"
              >
                Get embed code
              </Link>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a{" "}
              <strong>Knowledge Base Q&amp;A</strong> page widget from{" "}
              <Link to="/deployment" className="underline hover:text-blue-900">
                Deployments → Page Widgets
              </Link>{" "}
              for a lightweight, AI-only Q&amp;A experience — no live agent
              needed.
            </span>
          </div>
        </div>
      )}

      {loadError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {loadError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              hasConnection={hasActiveConfig}
              onConnect={() => navigate("/integrations/guesty/create")}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "accounts" && (
            <AccountsTab
              configs={configs}
              onRefresh={fetchConfigs}
              onAdd={() => navigate("/integrations/guesty/create")}
              onTogglePushTranscripts={togglePushTranscripts}
            />
          )}
          {activeTab === "ai-tools" && (
            <AiToolsTab
              hasConnection={hasActiveConfig}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "field-reference" && <FieldReferenceTab />}
          {activeTab === "logs" && (
            <SectionCard>
              <IntegrationActivityLog integrationSlug="Guesty" />
            </SectionCard>
          )}
        </>
      )}
    </StickyRouteHeader>
  );
}
